import { CanDeactivate, Router, NavigationEnd } from '@angular/router';
import { Injectable } from '@angular/core';
import { Observable, filter, take } from 'rxjs';

export interface ComponentCanDeactivate {
  canDeactivate: () => boolean | Observable<boolean>;
}

@Injectable({ providedIn: 'root' })
export class PendingChangesGuard implements CanDeactivate<ComponentCanDeactivate> {
  private currentUrl: string = '';

  constructor(private router: Router) {
    // Listen to router events to track the current URL
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        take(1)
      )
      .subscribe((event: NavigationEnd) => {
        this.currentUrl = event.urlAfterRedirects;
      });
  }

  canDeactivate(
    component: ComponentCanDeactivate
  ): boolean | Observable<boolean> {
    // Skip prompt if navigating to login page
    if (this.currentUrl === '/auth/login') return true;

    // Safely call canDeactivate()
    const result = component.canDeactivate?.();

    if (typeof result === 'boolean') {
      const shouldProceed = result || confirm(
        'WARNING: If you leave this page, your form details will be lost and you will need to place your order again.'
      );

      // Clear localStorage data if user confirms navigation
      if (shouldProceed) {
        this.clearPendingOrderData();
      }

      return shouldProceed;
    }

    if (result instanceof Observable) {
      return new Observable<boolean>((observer) => {
        result.subscribe((val) => {
          if (val) {
            observer.next(true);
          } else {
            const confirmResult = confirm(
              'WARNING: If you leave this page, your form details will be lost and you will need to place your order again.'
            );

            // Clear localStorage data if user confirms navigation
            if (confirmResult) {
              this.clearPendingOrderData();
            }

            observer.next(confirmResult);
          }
          observer.complete();
        });
      });
    }

    return true;
  }

  private clearPendingOrderData() {
    // Clear all pending order data from localStorage
    localStorage.removeItem('PENDINGHEAVYORDERDATA');
    localStorage.removeItem('PENDINGORDERDATA');
    localStorage.removeItem('PENDINGBULKORDERDATA');
    // Preserve inter-regional pending data if preservation flag is set
    const preserveInterRegional = sessionStorage.getItem('PRESERVE_PENDING_INTERREGIONALORDERDATA') === '1';
    if (!preserveInterRegional) {
      localStorage.removeItem('PENDINGINTERREGIONALORDERDATA');
    }
  }
}
